import React, { useState, useEffect } from 'react';
import { Settings, Save, DollarSign, Percent, Clock, Shield, CreditCard, AlertTriangle } from 'lucide-react';
import toast from 'react-hot-toast';
import { updateSettings, getSettings } from '../services/adminService';

const SettingsPanel = () => {
  const [settings, setSettings] = useState({
    // Pricing Settings
    driversDen: {
      weekday: { basePrice: 150, hourlyRate: 150 },
      weekend: { basePrice: 200, hourlyRate: 200 }
    },
    driversDen: {
      weekday: { basePrice: 250, hourlyRate: 200 },
      weekend: { basePrice: 300, hourlyRate: 300 }
    },
    
    // Add-ons Pricing
    addOns: {
      instruction: { name: 'Professional Instruction', price: 100 },
      photo: { name: 'Photo/Video Package', price: 50 }
    },
    
    // Business Settings
    depositPercentage: 25,
    cancellationPolicy: {
      freeCancellationHours: 48,
      cancellationFeePercentage: 10
    },
    
    // Stripe Settings
    stripe: {
      publishableKey: '',
      webhookSecret: '',
      connected: false
    },
    
    // Email Settings
    email: {
      enabled: true,
      fromEmail: 'noreply@digitaldivot.com',
      adminEmail: 'admin@digitaldivot.com'
    }
  });

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    setLoading(true);
    try {
      const result = await getSettings();
      if (result.settings) {
        setSettings(result.settings);
      }
    } catch (error) {
      console.error('Error loading settings:', error);
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSettings = async () => {
    setSaving(true);
    try {
      await updateSettings(settings);
      toast.success('Settings saved successfully!');
    } catch (error) {
      console.error('Error saving settings:', error);
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handlePricingChange = (packageName, dayType, field, value) => {
    setSettings(prev => ({
      ...prev,
      [packageName]: {
        ...prev[packageName],
        [dayType]: {
          ...prev[packageName][dayType],
          [field]: parseFloat(value)
        }
      }
    }));
  };

  const handleAddOnChange = (addOnId, field, value) => {
    setSettings(prev => ({
      ...prev,
      addOns: {
        ...prev.addOns,
        [addOnId]: {
          ...prev.addOns[addOnId],
          [field]: field === 'price' ? parseFloat(value) : value
        }
      }
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-600"></div>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
      {/* Header */}
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: '1rem' }}>
        <div>
          <h2 style={{ fontSize: '1.5rem', fontWeight: 700, color: '#ffffff', margin: 0 }}>System Settings</h2>
          <p style={{ color: '#d1d5db', marginTop: '0.3rem' }}>Configure pricing, policies, and integrations</p>
        </div>
        <button
          onClick={handleSaveSettings}
          disabled={saving}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            padding: '0.625rem 1rem',
            backgroundColor: '#C9A84C',
            color: '#000000',
            fontWeight: 700,
            borderRadius: '0.5rem',
            border: 'none',
            cursor: saving ? 'not-allowed' : 'pointer',
            opacity: saving ? 0.6 : 1,
          }}
        >
          <Save size={20} style={{ marginRight: '0.5rem' }} />
          {saving ? 'Saving...' : 'Save Settings'}
        </button>
      </div>

      {/* Pricing Settings */}
      <div className="bg-gray-800 rounded-lg border border-gray-700 p-6">
        <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
          <DollarSign size={20} className="mr-2" />
          Package Pricing
        </h3>
        
        <div className="space-y-6">
          {/* Driver's Den Pricing */}
          <div className="bg-gray-700 rounded-lg p-4">
            <h4 className="text-md font-medium text-white mb-4">Driver's Den Package</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h5 className="text-sm font-medium text-gray-300 mb-3">Weekday Pricing</h5>
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">Base Price</label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={settings.driversDen.weekday.basePrice}
                      onChange={(e) => handlePricingChange('driversDen', 'weekday', 'basePrice', e.target.value)}
                      className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-yellow-600"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">Hourly Rate</label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={settings.driversDen.weekday.hourlyRate}
                      onChange={(e) => handlePricingChange('driversDen', 'weekday', 'hourlyRate', e.target.value)}
                      className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-yellow-600"
                    />
                  </div>
                </div>
              </div>
              
              <div>
                <h5 className="text-sm font-medium text-gray-300 mb-3">Weekend Pricing</h5>
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">Base Price</label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={settings.driversDen.weekend.basePrice}
                      onChange={(e) => handlePricingChange('driversDen', 'weekend', 'basePrice', e.target.value)}
                      className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-yellow-600"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">Hourly Rate</label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={settings.driversDen.weekend.hourlyRate}
                      onChange={(e) => handlePricingChange('driversDen', 'weekend', 'hourlyRate', e.target.value)}
                      className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-yellow-600"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          {/* Driver's Den Pricing */}
          <div className="bg-gray-700 rounded-lg p-4">
            <h4 className="text-md font-medium text-white mb-4">Driver's Den Package</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h5 className="text-sm font-medium text-gray-300 mb-3">Weekday Pricing</h5>
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">Base Price</label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={settings.driversDen.weekday.basePrice}
                      onChange={(e) => handlePricingChange('driversDen', 'weekday', 'basePrice', e.target.value)}
                      className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-yellow-600"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">Hourly Rate</label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={settings.driversDen.weekday.hourlyRate}
                      onChange={(e) => handlePricingChange('driversDen', 'weekday', 'hourlyRate', e.target.value)}
                      className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-yellow-600"
                    />
                  </div>
                </div>
              </div>
              
              <div>
                <h5 className="text-sm font-medium text-gray-300 mb-3">Weekend Pricing</h5>
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">Base Price</label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={settings.driversDen.weekend.basePrice}
                      onChange={(e) => handlePricingChange('driversDen', 'weekend', 'basePrice', e.target.value)}
                      className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-yellow-600"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">Hourly Rate</label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={settings.driversDen.weekend.hourlyRate}
                      onChange={(e) => handlePricingChange('driversDen', 'weekend', 'hourlyRate', e.target.value)}
                      className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-yellow-600"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Add-ons Pricing */}
      <div className="bg-gray-800 rounded-lg border border-gray-700 p-6">
        <h3 className="text-lg font-semibold text-white mb-4">Add-ons Pricing</h3>
        
        <div className="space-y-4">
          {Object.entries(settings.addOns).map(([addOnId, addOn]) => (
            <div key={addOnId} className="bg-gray-700 rounded-lg p-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Add-on Name
                  </label>
                  <input
                    type="text"
                    value={addOn.name}
                    onChange={(e) => handleAddOnChange(addOnId, 'name', e.target.value)}
                    className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-yellow-600"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Price
                  </label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={addOn.price}
                    onChange={(e) => handleAddOnChange(addOnId, 'price', e.target.value)}
                    className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-yellow-600"
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Business Settings */}
      <div className="bg-gray-800 rounded-lg border border-gray-700 p-6">
        <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
          <Shield size={20} className="mr-2" />
          Business Settings
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Deposit Percentage
            </label>
            <div className="relative">
              <input
                type="number"
                min="0"
                max="100"
                value={settings.depositPercentage}
                onChange={(e) => setSettings(prev => ({
                  ...prev,
                  depositPercentage: parseInt(e.target.value)
                }))}
                className="w-full px-3 py-2 pr-8 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-yellow-600"
              />
              <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400">%</span>
            </div>
            <p className="text-sm text-gray-400 mt-1">
              Percentage of total cost required as deposit
            </p>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Free Cancellation Hours
            </label>
            <input
              type="number"
              min="0"
              max="168"
              value={settings.cancellationPolicy.freeCancellationHours}
              onChange={(e) => setSettings(prev => ({
                ...prev,
                cancellationPolicy: {
                  ...prev.cancellationPolicy,
                  freeCancellationHours: parseInt(e.target.value)
                }
              }))}
              className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-yellow-600"
            />
            <p className="text-sm text-gray-400 mt-1">
              Hours before booking for free cancellation
            </p>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Cancellation Fee Percentage
            </label>
            <div className="relative">
              <input
                type="number"
                min="0"
                max="100"
                value={settings.cancellationPolicy.cancellationFeePercentage}
                onChange={(e) => setSettings(prev => ({
                  ...prev,
                  cancellationPolicy: {
                    ...prev.cancellationPolicy,
                    cancellationFeePercentage: parseInt(e.target.value)
                  }
                }))}
                className="w-full px-3 py-2 pr-8 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-yellow-600"
              />
              <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400">%</span>
            </div>
            <p className="text-sm text-gray-400 mt-1">
              Fee percentage for cancellations after free period
            </p>
          </div>
        </div>
      </div>

      {/* Stripe Integration */}
      <div className="bg-gray-800 rounded-lg border border-gray-700 p-6">
        <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
          <CreditCard size={20} className="mr-2" />
          Stripe Payment Integration
        </h3>
        
        <div className="space-y-4">
          <div className="flex items-center justify-between p-4 bg-gray-700 rounded-lg">
            <div className="flex items-center space-x-3">
              <div className={`w-3 h-3 rounded-full ${settings.stripe.connected ? 'bg-green-500' : 'bg-red-500'}`}></div>
              <span className="text-white">
                {settings.stripe.connected ? 'Connected' : 'Not Connected'}
              </span>
            </div>
            <button className="px-4 py-2 bg-yellow-600 text-black font-semibold rounded-lg hover:bg-yellow-500 transition-colors">
              {settings.stripe.connected ? 'Reconnect' : 'Connect Stripe'}
            </button>
          </div>
          
          {!settings.stripe.connected && (
            <div className="p-4 bg-yellow-900/20 border border-yellow-600/30 rounded-lg">
              <div className="flex items-start space-x-3">
                <AlertTriangle className="text-yellow-400 mt-0.5" size={20} />
                <div>
                  <p className="text-yellow-200 font-medium">Stripe Not Connected</p>
                  <p className="text-yellow-300 text-sm mt-1">
                    Connect your Stripe account to enable payment processing for bookings.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Email Settings */}
      <div className="bg-gray-800 rounded-lg border border-gray-700 p-6">
        <h3 className="text-lg font-semibold text-white mb-4">Email Settings</h3>
        
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <label className="text-sm font-medium text-gray-300">Email Notifications</label>
              <p className="text-sm text-gray-400">Send booking confirmations and notifications</p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={settings.email.enabled}
                onChange={(e) => setSettings(prev => ({
                  ...prev,
                  email: { ...prev.email, enabled: e.target.checked }
                }))}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-600 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-yellow-600/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-yellow-600"></div>
            </label>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                From Email
              </label>
              <input
                type="email"
                value={settings.email.fromEmail}
                onChange={(e) => setSettings(prev => ({
                  ...prev,
                  email: { ...prev.email, fromEmail: e.target.value }
                }))}
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-yellow-600"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Admin Email
              </label>
              <input
                type="email"
                value={settings.email.adminEmail}
                onChange={(e) => setSettings(prev => ({
                  ...prev,
                  email: { ...prev.email, adminEmail: e.target.value }
                }))}
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-yellow-600"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SettingsPanel;
